import React, { useMemo, useState } from 'react'
import { Box, Autocomplete, TextField, Typography, IconButton, Collapse } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import { SupersetConfigItem } from '../editConfigurationTypes'

export interface DomainSelectorProps {
    supersetConfiguration: SupersetConfigItem[]
    domains: string[]
    domainTypesByDomain: { [domainName: string]: string[] }
    selectedDomain?: string
    selectedDomainType?: string
    onDomainChange: (domainName: string) => void
    onDomainTypeChange: (domainType: string) => void
    formData: any
}

const DomainSelector: React.FC<DomainSelectorProps> = ({
    supersetConfiguration,
    domains,
    domainTypesByDomain,
    selectedDomain,
    selectedDomainType,
    onDomainChange,
    onDomainTypeChange,
    formData
}) => {
    const [isFormatExpanded, setIsFormatExpanded] = useState(false)
    const [isDomainValuesExpanded, setIsDomainValuesExpanded] = useState(false)

    const domainTypes = selectedDomain ? (domainTypesByDomain[selectedDomain] || []) : []
    
    const handleDomainChange = (event: any, newValue: string | null) => {
        onDomainChange(newValue || '')
    }
    
    const handleDomainTypeChange = (event: any, newValue: string | null) => {
        onDomainTypeChange(newValue || '')
    }
    const getFormat = useMemo(() => (domainName: string, domainType: string) => {
        // from the superset get the format 
        if(!domainName || !domainType) return ""
        return supersetConfiguration.find((item) => item.domainName === domainName && item.domainType === domainType)?.format
    }, [supersetConfiguration,selectedDomain,selectedDomainType])
    return (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', mb: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 2, width: '100%' , flexDirection: 'row'}}>
            <Autocomplete
                options={domains}
                value={selectedDomain || null}
                onChange={handleDomainChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Domain"
                        size="small"
                        sx={{ width: '100%' }}
                    />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                sx={{ width: '100%' }}
            />
            
            <Autocomplete
                options={domainTypes}
                value={selectedDomainType || null}
                onChange={handleDomainTypeChange}
                disabled={!selectedDomain}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Domain Type"
                        size="small"
                        sx={{ width: '100%' }}
                    />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                sx={{ width: '100%' }}
            />

            </Box>
            
            <Box sx={{ width: '100%' }}>
                {getFormat(selectedDomain??"",selectedDomainType??"") && (
                    <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2">Format</Typography>
                        <IconButton
                            onClick={() => setIsFormatExpanded(!isFormatExpanded)}
                            size="small"
                            sx={{ p: 0.5 }}
                        >
                            {isFormatExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                    <Collapse in={isFormatExpanded}>
                        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: 'grey.100', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, width: '100%', height: '200px', overflow: 'auto' }}>
                          {typeof getFormat(selectedDomain??"",selectedDomainType??"") === 'string' ? getFormat(selectedDomain??"",selectedDomainType??"") : JSON.stringify(getFormat(selectedDomain??"",selectedDomainType??""), null, 2)}
                        </Box>
                    </Collapse>
                    </>
                )}
            </Box>
            <Box sx={{ width: '100%' }}>
                {formData && getFormat(selectedDomain??"",selectedDomainType??"") && (
                    <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2">Domain Values</Typography>
                        <IconButton
                            onClick={() => setIsDomainValuesExpanded(!isDomainValuesExpanded)}
                            size="small"
                            sx={{ p: 0.5 }}
                        >
                            {isDomainValuesExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </Box>
                    <Collapse in={isDomainValuesExpanded}>
                        <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', bgcolor: 'grey.100', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: 12, width: '100%', height: '300px', overflow: 'auto' }}>
                          {typeof formData === 'string' ? formData : JSON.stringify(formData, null, 2)}
                        </Box>
                    </Collapse>
                    </>
                )}
            </Box>
        </Box>
    )
}

export default DomainSelector


